// absolute imports
import { useMutation, useQuery } from '@apollo/client';
import { setUser as setSentryUserId } from '@sentry/react';
import {
  rootRouteId,
  useLocation,
  useRouteContext,
  useSearch,
} from '@tanstack/react-router';
import _isEqual from 'lodash/isEqual';
import _isNull from 'lodash/isNull';
import _omitBy from 'lodash/omitBy';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

// relative imports
import {
  CREATE_LEDGIBLE_USER,
  UPDATE_LEDGIBLE_USER,
} from '../../data/apollo/mutations';
import {
  GET_LEDGIBLE_USER,
  PENDING_LINK_QUERY,
} from '../../data/apollo/queries';
import { clearCacheForLogout } from '../shared/utilities/appUtilities';
/* Format oidc-compliant custom claims from auth0 to standard Auth0User graphql type fields */
export const formatAuth0UserForDatabase = (auth0User) => {
  if (!auth0User) {
    return {};
  }

  const formattedUser = { ...auth0User };
  const namespacedProperties = Object.keys(formattedUser).filter((key) =>
    key.includes('https://dashboard.ledgible.io/', 0),
  );
  const customPropertiesToStoreOnLedgibleUser = [
    'created_at',
    'last_password_reset',
    'user_metadata',
    'ledgible_tmp_user_token',
  ];

  // assign namespaced fields as root obj properties, then delete namespaced properties to align with GQL schema
  namespacedProperties.forEach((property) => {
    const propertyName = property.substring(30);

    if (customPropertiesToStoreOnLedgibleUser.includes(propertyName)) {
      formattedUser[propertyName] = formattedUser[property];
      delete formattedUser[property];
    }

    delete formattedUser[property];
  });

  return formattedUser;
};

/* Format Ledgible user for comparison to auth0 id token claims (user) value */
const formatLedgibleUserForProfileComparison = (ledgibleUser, subId) => {
  const formattedUser = _omitBy(ledgibleUser.profile, _isNull);
  formattedUser.user_metadata = _omitBy(formattedUser.user_metadata, _isNull);
  formattedUser.nickname = ledgibleUser.profile.username;
  formattedUser.name = ledgibleUser.profile.email;
  formattedUser.sub = subId;

  delete formattedUser.username;
  delete formattedUser.__typename;
  delete formattedUser.user_metadata.__typename;

  return formattedUser;
};

// implement context
export const LedgibleAuthContext = createContext();
export const useLedgibleAuth = () => useContext(LedgibleAuthContext);

export const LedgibleAuthProvider = ({ children }) => {
  const { auth0 = {} } = useRouteContext({
    from: rootRouteId,
  });
  const { pathname } = useLocation();
  const search = useSearch({ strict: false });
  const { tmpUserToken, codeInviteToken } = search;
  const userCreationToken = tmpUserToken || codeInviteToken;
  const [idTokenClaims, setIdTokenClaims] = useState(null);

  // Ensure auth0 context is properly populated
  // If auth0 is empty object or missing required properties, treat as not ready
  const hasAuth0Context =
    auth0 && typeof auth0 === 'object' && 'isLoading' in auth0;

  const {
    isAuthenticated = false,
    isLoading = true,
    user: auth0User,
    logout: auth0Logout,
  } = hasAuth0Context ? auth0 : {};

  const formattedAuth0User = useMemo(
    () =>
      formatAuth0UserForDatabase(
        idTokenClaims
          ? Object.fromEntries(
              Object.keys(auth0User)
                .filter((key) => key in idTokenClaims)
                .map((key) => [key, idTokenClaims[key]]),
            )
          : auth0User,
      ),
    [auth0User, idTokenClaims],
  );

  useEffect(() => {
    if (hasAuth0Context && auth0.getIdTokenClaims) {
      (async () => {
        try {
          const tokenClaims = await auth0.getIdTokenClaims();
          setIdTokenClaims(tokenClaims);
        } catch (error) {
          console.error('Error getting id token claims:', error);
        }
      })();
    }
  }, [auth0, hasAuth0Context]);

  const [updateLedgibleUser] = useMutation(UPDATE_LEDGIBLE_USER, {
    variables: {
      auth0User: formattedAuth0User,
    },
  });

  const [createLedgibleUser, { loading: creatingLedgibleUser }] = useMutation(
    CREATE_LEDGIBLE_USER,
    {
      variables: {
        auth0User: formattedAuth0User,
        token: userCreationToken,
      },
      refetchQueries: () => [GET_LEDGIBLE_USER],
    },
  );

  // Use idTokenClaims if available, otherwise fall back to auth0User
  // This handles the case where idTokenClaims hasn't been fetched yet
  const userClaims = idTokenClaims || auth0User;

  const skipQuery =
    isLoading ||
    !isAuthenticated ||
    (!userClaims?.email_verified && pathname !== '/verify-email');

  const {
    client,
    loading: loadingUser,
    data: { user } = {},
    refetch: refetchUser,
  } = useQuery(GET_LEDGIBLE_USER, {
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
    onCompleted: ({ user: result } = {}) => {
      if (result) {
        setSentryUserId(result.id);
        // diff ledgible user with auth0 user and udpate if different
        const ledgibleUserFormatted = formatLedgibleUserForProfileComparison(
          result,
          formattedAuth0User.sub,
        );

        if (!_isEqual(ledgibleUserFormatted, formattedAuth0User)) {
          updateLedgibleUser();
        }
      } else if (userCreationToken) {
        createLedgibleUser();
      }
    },
    skip: skipQuery,
  });

  // Query for pending link by email to get application type for account creation
  // Must come after GET_LEDGIBLE_USER query so we can reference user in skip condition
  const {
    data: { checkPendingLinkCodeInvite: pendingLinkData } = {},
    loading: loadingPendingLink,
  } = useQuery(PENDING_LINK_QUERY, {
    variables: {
      email: auth0User?.email,
    },
    skip: !auth0User?.email || !auth0User?.email_verified,
    fetchPolicy: 'network-only',
  });

  // Use application type from pending link query
  const pendingApplicationTypeId = pendingLinkData?.clientApplicationTypeId;

  const loadingAuth = useMemo(
    () =>
      isLoading || loadingUser || creatingLedgibleUser || loadingPendingLink,
    [creatingLedgibleUser, isLoading, loadingUser, loadingPendingLink],
  );

  /**
   * local logout function to handle logout call to auth0 plus session cleanup and append any other stuff
   * @param {Object} [args] - function arguments
   * @param {Object} [args.auth0LogoutOptions={}] - options for auth0 logout function
   * @param {Object} [args.auth0LogoutOptions.logoutParams={}] - options for auth0 logout function
   * @param {Boolean} [args.auth0LogoutOptions.logoutParams.redirectTo] - where to send user after auth0 logout, must be registered in Auth0 Allowed Logout URLs
   * @param {Boolean} [args.auth0LogoutOptions.logoutParams.federated] - attempts logout of upstream identity provider, if supported
   * @param {Function} [args.auth0LogoutOptions.openUrl] - optional function to handle callback manually
   * @param {String} [args.auth0LogoutOptions.clientId] - optional clientId of app to logout from, defaults to instantiated Auth0 SDK client id
   * @param {Object} [args.queryParameters={}] - optional object to transpose query parameters onto returnTo URL
   */
  const logout = useCallback(
    async ({ auth0LogoutOptions = {}, queryParameters = {} } = {}) => {
      const { logoutParams: { returnTo, ...restLogoutParams } = {}, ...rest } =
        auth0LogoutOptions;

      const logoutCallbackUrl = queryString.stringifyUrl({
        url: returnTo || `${import.meta.env.REACT_APP_AUTH0_CALLBACK}/login`,
        query: {
          ...queryParameters,
        },
      });

      const logoutOptions = {
        logoutParams: {
          returnTo: rest.openUrl ? null : logoutCallbackUrl,
          ...restLogoutParams,
        },
        ...rest,
      };

      // clear cache for logout
      await clearCacheForLogout(client);
      // perform logout for current app with appropriate app redirect (if populated);
      await auth0Logout(logoutOptions);
    },
    [client, auth0Logout],
  );

  // memoize values
  const ledgibleAuthValues = useMemo(
    () => ({
      user,
      refetchUser,
      loadingAuth,
      isAuthenticated,
      applicationTypeIdForAccountCreation: pendingApplicationTypeId,
      logout,
    }),
    [
      user,
      loadingAuth,
      isAuthenticated,
      logout,
      refetchUser,
      pendingApplicationTypeId,
    ],
  );

  return (
    <LedgibleAuthContext.Provider value={ledgibleAuthValues}>
      {children}
    </LedgibleAuthContext.Provider>
  );
};

export const LedgibleAuthConsumer = LedgibleAuthContext.Consumer;

LedgibleAuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
